// HelpShell — the simulator-level help wrapper.
//
// Provides everything the help layer needs in one mount:
//   - HelpProvider (context + state)
//   - "?" button anchored to the panel's top-right
//   - menu popover, welcome card, keyboard cheat card
//   - hotspot layer (annotates panel controls in hotspot mode)
//
// The shell wraps its children in a `position: relative` container.
// Hotspot targets live anywhere inside the children — the layer
// queries `[data-hotspot-id]` selectors against the wrapper and
// measures live element rects, so the panel can be re-laid-out
// without bbox values going stale.
//
// When the panel kind has no help data (e.g. all empty arrays), the
// help button hides and this shell is visually indistinguishable
// from a passthrough.
//
// Embed context (see ./embed-context): in "slide" the "?" button and
// the auto-welcome card are suppressed because the dialogs are
// designed for Lab-scale canvases and overflow the slide content
// area. Hotspot annotations stay; they live inside the panel
// surface and don't pop out as floating dialogs.

import type { ReactNode } from "react";
import { HelpProvider } from "./help-context";
import { HelpButton } from "./help-button";
import { HelpPopover } from "./help-popover";
import { WelcomeCard } from "./welcome-card";
import { KeyboardCheatCard } from "./keyboard-cheat-card";
import { HotspotLayer } from "./hotspot-overlay";
import { useEmbedContext } from "./embed-context";
import type { PanelKind } from "./help-types";

interface Props {
  panelKind: PanelKind;
  children: ReactNode;
}

export function HelpShell({ panelKind, children }: Props) {
  const embed = useEmbedContext();
  const showHelpDialogs = embed === "lab";
  return (
    <HelpProvider panelKind={panelKind}>
      <div style={{ position: "relative" }}>
        {children}
        <HotspotLayer />
        {showHelpDialogs && <HelpButton />}
        {showHelpDialogs && <HelpPopover />}
        {showHelpDialogs && <WelcomeCard />}
        {showHelpDialogs && <KeyboardCheatCard />}
      </div>
    </HelpProvider>
  );
}
